import { useQueryClient } from '@tanstack/react-query';

import type { MeetingRecap } from '@/types/review/PostReviewMeeting';
import type { QueryKey } from '@tanstack/react-query';

import { useAiGeneration } from '@/hooks/ai/useAiGeneration';

/** What every cached transcript carries about its recap, whichever journey it belongs to. */
interface TranscriptWithRecap {
    recap: MeetingRecap | null;
    pendingRecapCorrelationId: string | null;
}

interface UseTranscriptRecapParams {
    /** The meeting or the assessment the transcript belongs to; nothing is asked for without it. */
    subjectId: string | undefined;
    /** A generation the server reports still running for the transcript. */
    pendingCorrelationId?: string | null;
    /** Where the journey caches its transcript. */
    transcriptQueryKey: (subjectId: string) => QueryKey;
    /** Starts, or attaches to, the generation of the recap on the journey's own endpoint. */
    requestRecap: Parameters<typeof useAiGeneration>[0]['request'];
}

/**
 * Asks for the AI recap of an uploaded transcript and receives it over SignalR. Opening the screen
 * never asks for anything: when the server reports a generation still running, the hook waits on it.
 *
 * The recap is read-only, so the delivered content is written straight into the transcript cache
 * rather than refetched: the server has already validated its shape and retried before sending, and
 * a GET afterwards would only ask for what just arrived.
 */
export const useTranscriptRecap = ({
    subjectId,
    pendingCorrelationId,
    transcriptQueryKey,
    requestRecap,
}: UseTranscriptRecapParams) => {
    const queryClient = useQueryClient();

    return useAiGeneration({
        subjectId,
        pendingCorrelationId,
        request: requestRecap,
        onResult: (id, content) => {
            const recap = JSON.parse(content ?? '') as MeetingRecap;
            queryClient.setQueryData<TranscriptWithRecap>(transcriptQueryKey(id), (previous) =>
                previous
                    ? {
                          ...previous,
                          recap,
                          pendingRecapCorrelationId: null,
                          // A transcript that records who wrote its recap now has the AI's.
                          ...('isAiGenerated' in previous && { isAiGenerated: true }),
                      }
                    : previous,
            );
        },
        onGiveUp: (id) => void queryClient.invalidateQueries({ queryKey: transcriptQueryKey(id) }),
    });
};
